import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'

import { getGuestActionDescription } from '@/pages/clients/client-profile/recent-activities/recent-activities.guest'

import Icon from '../../Icons'

const RecentActivity = ({ recentActivity, timezone }) => {
  const { t } = useTranslation(['clients-legacy', 'clients'])

  const getTime = (dateTime) => {
    const hours = moment(dateTime)
      .tz(timezone || 'America/Chicago')
      .format('HH:mm')
    const day = moment(dateTime)
      .tz(timezone || 'America/Chicago')
      .format('Do')
    const month = moment(dateTime)
      .tz(timezone || 'America/Chicago')
      .format('MMMM')
    return t('clients-legacy:profileView.recentActivity.timeOnDayOfMonth', { day, hours, month })
  }

  const getTimeInText = (timestamp) => {
    return moment(timestamp).fromNow()
  }

  // TODO: this file still uses moment.js and a hardcoded 'America/Chicago' fallback instead of
  // @/utils/date + DEFAULT_TZ (see src/constants/date.ts) — needs migrating off the legacy pattern.
  const getReservationBody = (recentActivity) => {
    const amenityName = recentActivity.amenityId ? recentActivity.amenityId.name : ''
    const isMultiDay = Boolean(recentActivity.amenityId?.multiDayBooking && recentActivity.endDate)

    if (isMultiDay) {
      const startDate = moment(recentActivity.date)
        .tz(timezone || 'America/Chicago')
        .format('Do [of] MMMM')
      const endDate = moment(recentActivity.endDate)
        .tz(timezone || 'America/Chicago')
        .format('Do [of] MMMM')
      const interpolation = { amenityName, endDate, startDate }

      if (recentActivity.approved) {
        return t(
          'clients-legacy:profileView.recentActivity.reservationConfirmedMultiDay',
          interpolation,
        )
      }
      if (recentActivity.cancelled) {
        return t(
          'clients-legacy:profileView.recentActivity.reservationCanceledMultiDay',
          interpolation,
        )
      }
      return t(
        'clients-legacy:profileView.recentActivity.reservationRequestedMultiDay',
        interpolation,
      )
    }

    const startTime = moment(recentActivity.date + recentActivity.startTime * 1000 * 60)
      .tz(timezone || 'America/Chicago')
      .format('HH:mm')
    const endTime = moment(recentActivity.date + recentActivity.endTime * 1000 * 60)
      .tz(timezone || 'America/Chicago')
      .format('HH:mm')
    const day = moment(recentActivity.date)
      .tz(timezone || 'America/Chicago')
      .format('Do')
    const month = moment(recentActivity.date)
      .tz(timezone || 'America/Chicago')
      .format('MMMM')
    const interpolation = { amenityName, day, endTime, month, startTime }

    if (recentActivity.approved) {
      return t('clients-legacy:profileView.recentActivity.reservationConfirmed', interpolation)
    }
    if (recentActivity.cancelled) {
      return t('clients-legacy:profileView.recentActivity.reservationCanceled', interpolation)
    }
    return t('clients-legacy:profileView.recentActivity.reservationRequested', interpolation)
  }

  const getPackageBody = (recentActivity) => {
    const dateTime = recentActivity.updatedAt || recentActivity.createdAt

    if (recentActivity.signature) {
      return t('clients-legacy:profileView.recentActivity.packagePickedUp', {
        time: getTime(dateTime),
      })
    }
    return t('clients-legacy:profileView.recentActivity.packageArrived', {
      time: getTime(dateTime),
    })
  }

  const getNoteBody = (recentActivity) => recentActivity.name

  const getPostBody = (recentActivity) =>
    t('clients-legacy:profileView.recentActivity.postCreated', {
      time: getTime(recentActivity.updatedAt || recentActivity.createdAt),
    })

  const getMaintenanceBody = (recentActivity) =>
    t('clients-legacy:profileView.recentActivity.maintenanceStatusChanged', {
      status: recentActivity.status,
      time: getTime(recentActivity.updatedAt || recentActivity.createdAt),
    })

  const getGuestCheckInBody = (recentActivity) => {
    const actionDescription = getGuestActionDescription(recentActivity, t, timezone)
    if (actionDescription) return actionDescription

    return t('clients:recentActivities.descriptions.guest.checkedIn', {
      name: recentActivity.name,
      time: getTime(recentActivity.startTime),
    })
  }

  const isGuestAction = (recentActivity) =>
    Boolean(recentActivity.action) && recentActivity.action !== 'checkedIn'

  const getDataToShow = (recentActivity) => {
    switch (recentActivity.historyType) {
      case 'reservation':
        return {
          title: t('clients-legacy:profileView.recentActivity.reservationTitle'),
          icon: 'reservation',
          viewBox: '0 0 18 21',
          body: getReservationBody(recentActivity),
          timeOfActivity: getTimeInText(recentActivity.updatedAt || recentActivity.createdAt),
        }
      case 'guest':
        return {
          title: isGuestAction(recentActivity)
            ? t(`clients:recentActivities.categories.${recentActivity.historyType}`)
            : t('clients-legacy:profileView.recentActivity.guestCheckInTitle'),
          icon: 'guest',
          viewBox: '0 0 24 24',
          body: getGuestCheckInBody(recentActivity),
          timeOfActivity: getTimeInText(
            isGuestAction(recentActivity)
              ? recentActivity.createdAt
              : recentActivity.updatedAt || recentActivity.createdAt,
          ),
        }
      case 'package':
        return {
          title: t('clients:recentActivities.categories.package'),
          icon: 'package',
          viewBox: '0 0 18 21',
          body: getPackageBody(recentActivity),
          timeOfActivity: getTimeInText(recentActivity.updatedAt || recentActivity.createdAt),
        }
      case 'maintenance':
        return {
          title: t('clients:recentActivities.categories.maintenance'),
          icon: 'guest',
          viewBox: '0 0 24 24',
          body: getMaintenanceBody(recentActivity),
          timeOfActivity: getTimeInText(recentActivity.updatedAt || recentActivity.createdAt),
        }
      case 'note':
        return {
          title: t('clients-legacy:profileView.recentActivity.noteTitle'),
          icon: 'guest',
          viewBox: '0 0 24 24',
          body: getNoteBody(recentActivity),
          timeOfActivity: getTimeInText(recentActivity.updatedAt || recentActivity.createdAt),
        }
      case 'post':
        return {
          title: t('clients-legacy:profileView.recentActivity.socialFeedTitle'),
          icon: 'guest',
          viewBox: '0 0 24 24',
          body: getPostBody(recentActivity),
          timeOfActivity: getTimeInText(recentActivity.updatedAt || recentActivity.createdAt),
        }
      default:
        break
    }
  }

  const dataToShow = getDataToShow(recentActivity)

  return (
    <div
      key={`${recentActivity.historyType}-${recentActivity.id}`}
      className="recent-activity-block"
    >
      <div className="icon-chain">
        <div className="icon-wrapper">
          <Icon icon={dataToShow.icon} viewBox={dataToShow.viewBox} />
        </div>
        <div className="dotted-spaced" />
      </div>
      <div className="recent-activity-info-block">
        <div className="recent-activity-title-wrapper">
          <h3 className="recent-activity-title">{dataToShow.title}</h3>
        </div>
        <div className="recent-activity-body">
          <p className="body-text">{dataToShow.body}</p>
          <p className="time-text">{dataToShow.timeOfActivity}</p>
        </div>
      </div>
    </div>
  )
}

RecentActivity.propTypes = {
  recentActivity: PropTypes.object,
  timezone: PropTypes.string,
}

export default RecentActivity
